import styled, { useTheme } from "styled-components";
import Button from "../../components/Button/Button";
import { EntryCardProps } from "../../components/CheckoutEntryCard/CheckoutEntryCard";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useFormik, FormikProvider } from "formik";
import * as Yup from "yup";
import TextField from "../../components/FormFields/TextField";
import StyledForm from "../../components/Form/Form";
import DropdownField from "../../components/FormFields/DropdownField";
import {
	CanadaStateOptions,
	IndiaStateOptions,
	USStateOptions,
} from "../../data/StateData";
import UserMenu from "../Admin/User/UserMenu";
import { NavTabList } from "../../components/NavTabs/NavTabs";
import { UserPageContainer } from "../../components/UserPageTemplate/UserPageTemplate";
import SubmissionLightbox from "./SubmisisonLightbox";
import {
	createOrderandPdf,
	getFinalPrice,
	getDeadlinePrice,
	getCheckoutEntryCards,
	initPaymentzCheckout,
	priceOrderCheckout,
} from "./manageCheckout";
import {
	OrderLineItem,
	OrderModel,
	TransactionType,
	TransactionTypeOptions,
} from "./OrderInterfaces";
import { useCompany } from "../../hooks/UseCompany";
import { OrderSummaryDrawer } from "./CartTab";

import PrimaryLogo from "../../components/PrimaryLogo/PrimaryLogo";
import { WarningModal } from "../../components/Modal/Modal";
import { useHistory, useLocation } from "react-router-dom";
import config from "../../config";
import { commafy, commafySymbol } from "../../utils/commafy";
import { useAlert } from "../../components/Alert/Alerts";
import { PDFViewer } from "@react-pdf/renderer";
import InvoicePdf from "./InvoicePdf";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import Currency from "../../components/Currency/Currency";
import { getInvoiceLabels } from "../../utils/getLabels";
import DynamicDropdownField from "../../components/FormFields/DynamicDropdownField";
import { ThemisJudgment } from "../Admin/Themis/ThemisInterfaces";
import Loading from "../../components/Loading/Loading";
import { Footer } from "../../components/Footer/Footer";
import { copy } from "../../utils/copy";

const PaymentInfo = styled.div`
	padding: 2.25rem;
	border: solid 2px ${({ theme }) => theme.colorBorderLight};
	background: ${({ theme }) => theme.colorBackgroundLightLight};
`;

const LoadingSpinner = styled.img`
	width: 11%;
	display: inline-flex;
`;

const Payment = (props: PaymentProps) => {
	const theme = useTheme();
	const history = useHistory();
	const { settings } = useSelector((state: RootState) => state.settings);
	const location = useLocation<LocationState>();
	const { addNewAlert } = useAlert();
	const { company, setCompany } = useCompany();
	const [order, setOrder] = useState<OrderModel>({
		companyAddress1: "",
		companyCity: "",
		companyCountry: "",
		companyName: "",
		companyPostalCode: "",
		companyState: "",
		email: "",
		fullName: "",
		lineItems: [],
		orderTotal: 0,
		discountTotal: 0,
		feeTotal: 0,
		subtotal: 0,
		taxTotal: 0,
		phone: "",
		statusId: 0,
		transactionType: props.transactionType,
		bankCity: "",
		bankName: "",
		cardInfo: {
			cardCode: "",
			cardNumber: "",
			expirationDate: "",
		},
		companyAddress2: "",
		notes: "",
		purchaseOrder: "",
		taxId: company.taxID || "",
	});

	const [itemCount, setItemCount] = useState<number>(0);
	const [itemText, setItemText] = useState("");
	const [priceChangeModal, setPriceChangeModal] = useState(false);
	const [judgment, setJudgment] = useState<ThemisJudgment | null>(null);
	const [pricingLoading, setPricingLoading] = useState(false);
	const [pricingError, setPricingError] = useState<string | null>(null);
	const orderTotal = judgment?.total ?? location.state.orderTotalFinal; // fallback during transition
	const [paymentzLoading, setPaymentzLoading] = useState(false);

	let countryIso = settings.countryIso;

	const [drawerEl, setDrawerEl] = useState<HTMLElement | null>(null);
	const [drawerHeight, setDrawerHeight] = useState(0);
	const drawerRef = useCallback(
		(node: HTMLElement | null) => setDrawerEl(node),
		[],
	);

	const [offlineSubText, setOfflineSubText] = useState<string>();
	const [invoiceNotesSubtext, setInvoiceNotesSubtext] = useState<string>();

	useEffect(() => {
		copy("offlineSubText")
			.then((text) => {
				setOfflineSubText(text);
			})
			.catch();
		copy("invoiceNotesSubtext")
			.then((text) => {
				setInvoiceNotesSubtext(text);
			})
			.catch();
	}, []);

	// Reserve scroll space so the fixed drawer never covers content
	useEffect(() => {
		if (!drawerEl) return;

		const updateHeight = () => {
			const newHeight = drawerEl.offsetHeight;
			requestAnimationFrame(() => setDrawerHeight(newHeight));
		};

		const ro = new ResizeObserver(updateHeight);
		ro.observe(drawerEl);
		updateHeight();
		return () => ro.disconnect();
	}, [drawerEl]);

	const handleGetCheckoutEntryCards = () => {
		getCheckoutEntryCards().then((response) => {
			if (response.status == 200) {
				let orderLineItems: OrderLineItem[] = [];

				response.data.forEach((entry: EntryCardProps) => {
					let orderLineItem: OrderLineItem = {
						entryId: entry.id,
						isCampaign: entry.isCampaign ?? false,
						entry: entry,
					};
					orderLineItems.push(orderLineItem);
				});

				setItemCount(response.data.length);
				if (response.data.length > 1) {
					setItemText(response.data.length + " items in your cart");
				} else {
					setItemText(response.data.length + " item in your cart");
				}
				setOrder((prev) => {
					return {
						...prev,
						lineItems: orderLineItems,
					};
				});
			}
		});
	};

	useEffect(() => {
		handleGetCheckoutEntryCards();
	}, []);

	useEffect(() => {
		handleGetCheckoutEntryCards();
	}, [company]);

	useEffect(() => {
		setOrder((prev) => {
			return {
				...prev,
				orderTotal: orderTotal,
				companyAddress1: company.address1 || "",
				companyCity: company.city || "",
				companyCountry: company.country || "",
				companyName: company.name,
				companyPostalCode: company.postalCode || "",
				companyState: company.state || "",
				email: company.financeContactEmail || "",
				phone: company.financeContactPhone || "",
				companyAddress2: company.address2 || "",
				fullName: company.financeContactName || "",
				taxId: company.taxID || "",
			};
		});
	}, [company]);

	function submitToPaymentz(
		redirectUrl: string,
		fields: Record<string, string>,
	) {
		// set loading screen while redirecting to Paymentz
		setPaymentzLoading(true);

		// set session storage item to reload payment if user is returned from Paymentz
		sessionStorage.setItem("paymentz_redirect", "1");

		const form = document.createElement("form");
		form.method = "POST";
		form.action = redirectUrl;

		Object.entries(fields).forEach(([key, value]) => {
			const input = document.createElement("input");
			input.type = "hidden";
			input.name = key;
			input.value = value ?? "";
			form.appendChild(input);
		});

		document.body.appendChild(form);
		form.submit();
	}

	async function PostPayment(PaymentData: OrderModel, orderTotal: number) {
		// re-calculate price when submit payment is clicked
		const priced = await priceOrderCheckout(PaymentData);

		// only submit payment if display order total is the same as calculated price
		if (priced.total === orderTotal) {
			try {
				if (PaymentData.transactionType === TransactionType.Paymentz) {
					const init = await initPaymentzCheckout(PaymentData);
					submitToPaymentz(init.redirectUrl, init.fields);
					return;
				}

				// show confirmation modal once pdf is created
				const processedOrder = await createOrderandPdf(
					PaymentData,
					countryIso,
					PaymentData.taxId,
					settings.featureFlags["Invoice Notes"],
				);
				processedOrder.paymentDate = new Date(processedOrder.paymentDate);

				if (!processedOrder.data.isError) {
					setOrder(processedOrder.data);
				} else {
					throw new Error(processedOrder.data.authMessage);
				}
			} catch (error: any) {
				addNewAlert({
					type: "error",
					message: error.response?.data || error.message || String(error),
				});
			}
		} else {
			setPriceChangeModal(true);
			handleGetCheckoutEntryCards();
			return;
		}
	}

	// initial values for formik
	const creditCardValues = {
		cardNumber: "",
		expiration: "",
		cvv: "",
		cardZip: "",
	};

	// initial values for formik
	const bankTransferValues = {
		bankName: "",
		bankCity: "",
	};

	const fieldRequired = "This field is required";

	const creditCardValidation = {
		cardInfo: Yup.object().shape({
			cardNumber: Yup.string()
				.required(fieldRequired)
				.test("checkForNonDigits", "Must contain digits only", (val) => {
					return val === undefined ? false : /^\d+$/.test(val);
				})
				.test("checkLength", "Must be between 15 and 16 digits", (val) => {
					return val === undefined
						? false
						: val.length >= 15 && val.length <= 16;
				}),
			expirationDate: Yup.string()
				.required(fieldRequired)
				.test("checkForNonDigits", "Must contain digits only", (val) => {
					return val === undefined ? false : /^\d+$/.test(val);
				})
				.test("checkLength", "Must be exactly 4 digits", (val) => {
					return val === undefined ? false : val.length === 4;
				}),
			cardCode: Yup.string()
				.required(fieldRequired)
				.test("checkForNonDigits", "Must contain digits only", (val) => {
					return val === undefined ? false : /^\d+$/.test(val);
				})
				.test("checkLength", "Must be between 3 and 4 digits", (val) => {
					return val === undefined ? false : val.length >= 3 && val.length <= 4;
				}),
		}),
	};

	const formikProps = useFormik({
		initialValues: order,
		enableReinitialize: true,
		onSubmit: async (value) => {
			await PostPayment(value, orderTotal);
		},
		validationSchema: Yup.object({
			fullName: Yup.string()
				.required(fieldRequired)
				.max(100, "Full Name Maximum is 100 Characters"),
			email: Yup.string().email().required(fieldRequired),
			phone: Yup.string().required(fieldRequired),
			companyName: Yup.string().required(fieldRequired),
			companyAddress1: Yup.string().required(fieldRequired),
			companyCity: Yup.string().required(fieldRequired),
			companyCountry: Yup.string().required(fieldRequired),

			// use credit card validation
			...(order.transactionType === TransactionType.Credit && {
				...creditCardValidation,
			}),
		}),
		validateOnChange: true,
	});

	useEffect(() => {
		// Don’t price until we have line items and settings are loaded
		if (!order?.lineItems?.length) return;
		if (!settings || Object.values(settings).length === 0) return;

		let cancelled = false;

		(async () => {
			try {
				setPricingLoading(true);
				setPricingError(null);

				// Important: ensure the order has the latest TransactionType too
				const priced = await priceOrderCheckout({
					...order,
					transactionType: props.transactionType,
					companyCountry: formikProps.values.companyCountry,
					companyState: formikProps.values.companyState,
				});

				// notify users of a price difference b/w cart and checkout
				if (priced.subtotal !== location.state.orderTotalFinal) {
					setPriceChangeModal(true);
				}

				if (!cancelled) {
					setJudgment(priced);
					setOrder((prev) => ({ ...prev, orderTotal: priced.total }));
				}
			} catch (e: any) {
				if (!cancelled) {
					setJudgment(null);
					setPricingError(e?.message || "Unable to calculate pricing.");
				}
			} finally {
				setPricingLoading(false);
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [
		// inputs that should re-price
		props.transactionType,
		order.lineItems,
		order.subtotal,
		formikProps.values.companyCountry,
		formikProps.values.companyState,
		countryIso,
		settings,
	]);

	// refresh page if user is returned from Paymentz
	useEffect(() => {
		if (sessionStorage.getItem("paymentz_redirect") === "1") {
			sessionStorage.removeItem("paymentz_redirect");
			window.location.reload();
		}
	}, []);

	const [labels, setLabels] = useState<Record<string, string> | null>(null);
	useEffect(() => {
		getInvoiceLabels().then(setLabels);
	}, []);

	return paymentzLoading ? (
		<Loading text="Redirecting to Paymentz..." />
	) : (
		<div className="flex flex-col w-full">
			<WarningModal
				show={priceChangeModal}
				title="Price Change"
				message="The price of some of the entries in your cart may have changed. Please review before proceeding."
				close={() => setPriceChangeModal(false)}
			>
				<Button
					className="w-[225px]"
					onClick={() => setPriceChangeModal(false)}
				>
					Review Payment
				</Button>
			</WarningModal>
			{order.id && <SubmissionLightbox order={order} show={order !== null} />}

			<UserMenu />
			<NavTabList>
				<div className="flex items-center mb-[66px] flex-wrap gap-[2rem]">
					<PrimaryLogo
						className="mr-[5rem]"
						color={theme.colorLogoLightLight}
						width="254px"
					/>
					<h1 className="text-colorCopyLightLight">Checkout</h1>
				</div>
			</NavTabList>
			<UserPageContainer className="flex-1">
				<div style={{ paddingBottom: drawerHeight }}>
					<FormikProvider value={formikProps}>
						<h2 className="font-semibold">
							{props.transactionType === 4
								? TransactionTypeOptions[4].label
								: `${TransactionType[props.transactionType]} Payment`}
						</h2>
						{TransactionType[props.transactionType] === TransactionType[0] && (
							// Credit Payment
							<p className="mt-[1rem]">
								Please fill in the required credit card fields and invoice payor
								information. The invoice payor information and anything you
								enter in Invoice Notes will appear on your invoice.
							</p>
						)}
						{TransactionType[props.transactionType] === TransactionType[1] && (
							// Check Payment
							<p className="mt-[1rem]">
								By selecting Check Payment, your order will be moved to your
								Orders tab, but its status will remain pending until we receive
								and verify your payment. <br />
								The invoice payor information and anything you enter in Invoice
								Notes will appear on your invoice. <br />
								Be sure to include your Order ID in your check's note field.
							</p>
						)}
						{TransactionType[props.transactionType] === TransactionType[2] && (
							// Bank Payment
							<p className="mt-[1rem]">
								By selecting Bank Transfer your order will be moved to your
								Orders tab, but its status will remain pending until we receive
								and verify your payment. <br />
								The invoice payor information and anything you enter in Invoice
								Notes will appear on your invoice. <br />
								Be sure to include your Order ID in your transfer note.
							</p>
						)}
						{TransactionType[props.transactionType] === TransactionType[4] && (
							// Paymentz Payment
							<p className="mt-[1rem]">
								Please fill in the payor information. <br />
								Then click to proceed to Payment.
							</p>
						)}

						{TransactionType[props.transactionType] === TransactionType[5] &&
							// Offline Payment
							offlineSubText && (
								<p
									className="mt-[1rem]"
									dangerouslySetInnerHTML={{ __html: offlineSubText }}
								/>
							)}

						{/* a pdf preview below for testing purposes only */}
						{/* {labels && (
							<PDFViewer height={1800} width={1200}>
								<InvoicePdf
									order={formikProps.values}
									labels={labels}
									companyTaxId={formikProps.values.taxId}
									showInvoiceNotes={settings.featureFlags["Invoice Notes"]}
								/>
							</PDFViewer>
						)} */}

						<PaymentInfo className="mt-[3rem]">
							<h2 className="font-semibold mb-[3.5rem]">
								Invoice Payor Information
							</h2>
							<div className="flex flex-wrap gap-x-[3.5rem] gap-y-[2rem] lg:flex-nowrap">
								<StyledForm className="twoColumn" compact>
									<div className="col-w-100">
										<TextField
											name="fullName"
											placeholder="Full Name*"
											value={formikProps.values.fullName}
										/>
									</div>

									<TextField
										name="email"
										placeholder="Email Address*"
										value={formikProps.values.email}
									/>

									<TextField
										name="phone"
										placeholder="Phone Number*"
										value={formikProps.values.phone}
									/>
									<div className="col-w-100">
										<TextField
											name="companyName"
											placeholder="Company Name*"
											value={formikProps.values.companyName}
										/>
									</div>
									<div className="col-w-100">
										<TextField
											name="companyAddress1"
											placeholder="Address Line 1*"
											value={formikProps.values.companyAddress1}
										/>
									</div>
									<div className="col-w-100">
										<TextField
											name="companyAddress2"
											placeholder="Address Line 2"
											value={formikProps.values.companyAddress2}
										/>
									</div>
									<DynamicDropdownField
										name="companyCountry"
										placeholder="Company Country*"
										category="Country"
										saveLabelAsValue
										value={formikProps.values.companyCountry}
									/>
									{formikProps.values.companyCountry === "United States" ||
									formikProps.values.companyCountry === "Canada" ||
									formikProps.values.companyCountry === "India" ? (
										<DropdownField
											name="companyState"
											placeholder="Company State / Province / Region"
											options={
												formikProps.values.companyCountry === "United States"
													? USStateOptions
													: formikProps.values.companyCountry === "Canada"
														? CanadaStateOptions
														: IndiaStateOptions
											}
											value={formikProps.values.companyState}
										/>
									) : (
										<TextField
											name="companyState"
											placeholder="Company State / Province / Region"
											value={formikProps.values.companyState}
										/>
									)}
									<TextField
										name="companyCity"
										placeholder="Company City*"
										value={formikProps.values.companyCity}
									/>

									<TextField
										name="companyPostalCode"
										placeholder="Company Zip Code"
										value={formikProps.values.companyPostalCode}
									/>
								</StyledForm>

								{props.transactionType == TransactionType.Credit && (
									<StyledForm className="twoColumn mb-[1rem] h-min" compact>
										<h2 className="font-semibold">Credit Card Information</h2>
										<div className="col-w-100">
											<TextField
												name="cardInfo.cardNumber"
												placeholder="Card Number*"
												value={formikProps.values.cardInfo?.cardNumber}
												error={
													formikProps.errors.cardInfo &&
													(formikProps.errors.cardInfo as any).cardNumber
												}
											/>
										</div>

										<TextField
											name="cardInfo.expirationDate"
											placeholder="Expiry Date* (MMYY)"
											value={formikProps.values.cardInfo?.expirationDate}
											error={
												formikProps.errors.cardInfo &&
												(formikProps.errors.cardInfo as any).expirationDate
											}
										/>

										<TextField
											name="cardInfo.cardCode"
											placeholder="CVV*"
											value={formikProps.values.cardInfo?.cardCode}
											error={
												formikProps.errors.cardInfo &&
												(formikProps.errors.cardInfo as any).cardCode
											}
										/>

										{settings.featureFlags["Invoice Notes"] && (
											<div className="col-w-100">
												<TextField
													className="mb-[1rem]"
													name="notes"
													placeholder="Invoice Notes"
													component="textarea"
													height="200px"
													value={formikProps.values.notes}
												/>
												{invoiceNotesSubtext && (
													<p
														dangerouslySetInnerHTML={{
															__html: invoiceNotesSubtext,
														}}
													/>
												)}
											</div>
										)}
									</StyledForm>
								)}

								{(props.transactionType === TransactionType.Check ||
									props.transactionType === TransactionType.Offline ||
									props.transactionType === TransactionType.Paymentz) && (
									<StyledForm className="oneColumn mb-[1rem]" compact>
										<TextField
											name="purchaseOrder"
											placeholder="Purchase Order"
											value={formikProps.values.purchaseOrder}
										/>

										<TextField
											name="taxId"
											placeholder="Tax ID"
											value={formikProps.values.taxId}
										/>

										{settings.featureFlags["Invoice Notes"] && (
											<div className="col-w-100">
												<TextField
													className="mb-[1rem]"
													name="notes"
													placeholder="Invoice Notes"
													component="textarea"
													height="200px"
													value={formikProps.values.notes}
												/>
												{invoiceNotesSubtext && (
													<p
														dangerouslySetInnerHTML={{
															__html: invoiceNotesSubtext,
														}}
													/>
												)}
											</div>
										)}
									</StyledForm>
								)}

								{props.transactionType == TransactionType.Bank && (
									<StyledForm className="oneColumn mb-[1rem]" compact>
										<h2 className="font-semibold">Bank Information</h2>
										<div className="col-w-100">
											<TextField
												name="bankName"
												placeholder="Bank Name"
												value={formikProps.values.bankName}
											/>
										</div>
										<div className="col-w-100">
											<TextField
												name="bankCity"
												placeholder="Bank City"
												value={formikProps.values.bankCity}
											/>
										</div>
										<TextField
											name="purchaseOrder"
											placeholder="Purchase Order"
											value={formikProps.values.purchaseOrder}
										/>
										{settings.featureFlags["Invoice Notes"] && (
											<div className="col-w-100">
												<TextField
													className="mb-[1rem]"
													name="notes"
													placeholder="Invoice Notes"
													component="textarea"
													height="200px"
													value={formikProps.values.notes}
												/>
												{invoiceNotesSubtext && (
													<p
														dangerouslySetInnerHTML={{
															__html: invoiceNotesSubtext,
														}}
													/>
												)}
											</div>
										)}
									</StyledForm>
								)}
							</div>
						</PaymentInfo>

						<OrderSummaryDrawer ref={drawerRef}>
							<div className="pricing-container">
								<div className="flex flex-col gap-[1rem]">
									<h2>My Cart</h2>
									<p>{itemText}</p>
								</div>

								<div className="flex gap-[3rem]">
									<div className="flex flex-col mt-auto gap-[2rem]">
										<div className="flex flex-col mt-auto gap-[0.5rem] min-w-[260px]">
											{/* show loading text if pricing is loading or judgment is not available */}
											{(pricingLoading ||
												(!judgment && orderTotal !== 0 && !pricingError)) && (
												<div className="text-sm opacity-70">
													Calculating taxes…
												</div>
											)}

											{pricingError && (
												<div className="text-sm text-red-500">
													{pricingError}
												</div>
											)}

											{judgment && (
												<>
													<div className="flex justify-between">
														<span>Subtotal</span>
														<b>
															<Currency
																countryIso={countryIso}
																amount={judgment.subtotal}
															/>
														</b>
													</div>

													{judgment.discountTotal > 0 && (
														<div className="flex justify-between">
															<span>Discounts</span>
															<b>
																-
																<Currency
																	countryIso={countryIso}
																	amount={judgment.discountTotal}
																/>
															</b>
														</div>
													)}

													{/* GST breakdown lines (order-level only -> lineKey == null) */}
													{judgment.breakdown
														?.filter((x) => !x.lineKey && x.amount !== 0)
														?.map((x) => (
															<div
																key={x.code}
																className="flex justify-between"
															>
																<span>{x.label || x.code}</span>
																<b>
																	<Currency
																		countryIso={countryIso}
																		amount={x.amount}
																	/>
																</b>
															</div>
														))}

													{judgment.taxTotal > 0 && (
														<div className="flex justify-between">
															<span>Total Tax</span>
															<b>
																<Currency
																	countryIso={countryIso}
																	amount={judgment.taxTotal}
																/>
															</b>
														</div>
													)}

													<div className="border-t pt-2 mt-1 flex justify-between">
														<span>Total</span>
														<b>
															<Currency
																countryIso={countryIso}
																amount={judgment.total}
															/>
														</b>
													</div>
												</>
											)}

											{/* Fallback if judgment not available yet */}
											{/* {!judgment && orderTotal !== 0 && (
												<h2 className="whitespace-nowrap ml-auto ">
													Order Sub Total:{" "}
													<b>
														<Currency
															countryIso={countryIso}
															amount={orderTotal}
														/>
													</b>
												</h2>
											)} */}
										</div>

										<div className="flex flex-wrap gap-[1rem] justify-end">
											<Button
												className="button-light w-[250px]"
												to="/entries/cart"
												icon="close"
												iconSize="18px"
											>
												Cancel (Return to Cart)
											</Button>
											<Button
												className="w-[250px]"
												icon={formikProps.isSubmitting ? undefined : "check"}
												onClick={() => formikProps.submitForm()}
												disabled={formikProps.isSubmitting || itemCount === 0}
												type="submit"
											>
												<div className="flex items-center gap-[.5rem] order-1 whitespace-nowrap">
													{formikProps.isSubmitting && (
														<LoadingSpinner
															src={config.assets.loading.primary}
														/>
													)}
													{props.transactionType === TransactionType.Paymentz
														? `Proceed to Payment`
														: props.transactionType === TransactionType.Offline
															? "Submit Entries"
															: "Submit Payment"}
												</div>
											</Button>
										</div>
									</div>
								</div>
							</div>
							<Footer className="border-t border-gray-200" />
						</OrderSummaryDrawer>
					</FormikProvider>
				</div>
			</UserPageContainer>
		</div>
	);
};

export default Payment;

export interface PaymentProps {
	transactionType: TransactionType;
}

interface LocationState {
	orderTotalFinal: number;
}
